'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import {
  useGetProviderCohortByIdQuery,
  useGetProviderCohortsQuery,
} from 'src/api/redux';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faBuilding,
  faUser,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { stripHtml } from 'src/utils/stripHtml';
import {
  EncounterItem,
  encounterNodeConfig,
  encounterModalConfig,
  transformEncounter,
} from './config';

interface EncountersComponentProps {
  componentId: string;
  initialEncounters?: EncounterItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const EncountersComponent: React.FC<EncountersComponentProps> = ({
  componentId,
  initialEncounters = [],
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  const [encounters, setEncounters] =
    useState<EncounterItem[]>(initialEncounters);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [expandedSection, setExpandedSection] = useState<
    'company' | 'personal' | null
  >('company');

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialEncounters.length === 0
  );

  // Check if we need to hydrate from ID
  const needsHydration =
    initialEncounters.length > 0 && typeof initialEncounters[0] === 'string';
  const cohortId = needsHydration ? String(initialEncounters[0]) : null;

  const { data: hydratedData, isLoading: isHydrating } =
    useGetProviderCohortByIdQuery(cohortId as string, {
      skip: !needsHydration || !cohortId,
    });

  // Fetch encounter sets for the modal
  const { data: encounterSetsData, isLoading: isSearching } =
    useGetProviderCohortsQuery({
      page: 0,
      pageSize: 100,
    });

  // Filter by search and transform to EncounterItem format
  const searchResults: EncounterItem[] = useMemo(() => {
    let results = encounterSetsData?.results || [];

    // Apply search filter if present
    if (debouncedSearch) {
      results = results.filter((encounter: any) =>
        encounter.label?.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    return results.map(transformEncounter);
  }, [encounterSetsData?.results, debouncedSearch]);

  // Group encounters by scope for custom rendering
  const groupedEncounters = useMemo(() => {
    const companyEncounters = searchResults
      .filter((e) => e.scope?.toLowerCase() === 'company')
      .sort((a, b) => a.label.localeCompare(b.label));

    const personalEncounters = searchResults
      .filter((e) => e.scope?.toLowerCase() !== 'company')
      .sort((a, b) => a.label.localeCompare(b.label));

    return { companyEncounters, personalEncounters };
  }, [searchResults]);

  // Hydrate the encounter data when it loads
  useEffect(() => {
    if (needsHydration && hydratedData) {
      const transformed = transformEncounter(hydratedData);
      setEncounters([transformed]);
    }
  }, [needsHydration, hydratedData]);

  // Auto-open modal if starting with no encounters (new component)
  useEffect(() => {
    if (initialEncounters.length === 0 && !isHydrating) {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    if (initialEncounters.length > encounters.length) {
      setEncounters(initialEncounters);
    }
  }, [initialEncounters.length]);

  const handleSave = useCallback(
    (newEncounters: EncounterItem[], connector: ConnectorType) => {
      if (!newEncounters || newEncounters.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      const selectedEncounter = newEncounters[0];
      setEncounters([selectedEncounter]);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      // Encounters always use 'ALL' connector (single selection), but we accept the param for type compatibility
      onUpdate(componentId, {
        type: 'provider_cohort',
        value: selectedEncounter.id,
        ui_metadata: [selectedEncounter], // Store full object for hydration-free loading
        connector: connector || 'ALL',
      });

      setShowModal(false);
    },
    [componentId, onUpdate, onRemove]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && encounters.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, encounters.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  // Custom render function for grouped results (Company/Personal sections)
  const renderGroupedResults = useCallback(
    ({
      selectedItems,
      onSelect,
      isLoading,
      searchTerm: currentSearchTerm,
    }: {
      results: EncounterItem[];
      selectedItems: EncounterItem[];
      onSelect: (item: EncounterItem) => void;
      onDeselect: (item: EncounterItem) => void;
      isLoading: boolean;
      searchTerm: string;
    }) => {
      const hasResults =
        groupedEncounters.companyEncounters.length > 0 ||
        groupedEncounters.personalEncounters.length > 0;

      if (isLoading) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in duration-300">
              <div className="animate-pulse text-neutral-400">
                Loading encounter sets...
              </div>
            </div>
          </div>
        );
      }

      if (!hasResults) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex flex-col items-center gap-3">
                <Icon icon={faUsers} className="w-12 h-12 text-neutral-300" />
                <div className="flex flex-col items-center gap-1">
                  <div className="text-sm">
                    {currentSearchTerm
                      ? `No encounter sets found for "${currentSearchTerm}"`
                      : 'No encounter sets available'}
                  </div>
                  {currentSearchTerm && (
                    <div className="text-xs text-neutral-400">
                      Try adjusting your search terms
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      const renderSection = (
        title: string,
        icon: typeof faBuilding,
        items: EncounterItem[],
        sectionKey: 'company' | 'personal'
      ) => {
        if (items.length === 0) return null;

        return (
          <div key={sectionKey}>
            <button
              type="button"
              onClick={() =>
                setExpandedSection(
                  expandedSection === sectionKey ? null : sectionKey
                )
              }
              className="w-full bg-neutral-100 px-4 py-2.5 border-b border-neutral-200 flex items-center justify-between gap-2 hover:bg-neutral-200 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon icon={icon} className="w-3.5 h-3.5 text-neutral-500" />
                <span className="text-sm font-semibold text-neutral-700">
                  {title}
                </span>
                <span className="text-xs text-neutral-500">
                  ({items.length})
                </span>
              </div>
              <Icon
                icon={faChevronDown}
                className={`w-3 h-3 text-neutral-500 transition-transform duration-200 ${
                  expandedSection === sectionKey ? 'rotate-180' : ''
                }`}
              />
            </button>
            {expandedSection === sectionKey && (
              <div>
                {items.map((item) => {
                  const isSelected = selectedItems.some(
                    (s) => s.id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      onClick={() => onSelect(item)}
                      className={`p-4 border-b border-neutral-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-neutral-50 ${
                        isSelected ? 'bg-orange-50 hover:bg-orange-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ${
                              isSelected
                                ? 'border-orange-600 bg-orange-600'
                                : 'border-neutral-300'
                            }`}
                          >
                            {isSelected && (
                              <Icon
                                icon={faCheck}
                                className="w-2.5 h-2.5 text-white"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm truncate ${
                                isSelected
                                  ? 'text-orange-800'
                                  : 'text-neutral-900'
                              }`}
                            >
                              {stripHtml(item.label)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      };

      return (
        <div className="h-full overflow-y-auto border border-neutral-200 rounded-lg bg-white">
          {renderSection(
            'Company',
            faBuilding,
            groupedEncounters.companyEncounters,
            'company'
          )}
          {renderSection(
            'Personal',
            faUser,
            groupedEncounters.personalEncounters,
            'personal'
          )}
        </div>
      );
    },
    [groupedEncounters, expandedSection]
  );

  // Create config with custom render function
  const modalConfigWithRender = useMemo(
    () => ({
      ...encounterModalConfig,
      renderResultsSection: renderGroupedResults,
    }),
    [renderGroupedResults]
  );

  // Show loading state while hydrating data
  if (isHydrating) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500">
        Loading encounter set...
      </span>
    );
  }

  return (
    <>
      {encounters.length > 0 ? (
        <GenericNodeComponent
          config={encounterNodeConfig}
          items={encounters}
          connector="ALL"
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 border border-orange-200 rounded-full cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-orange-600 text-xs font-medium">
            Select encounter set...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={modalConfigWithRender}
        isOpen={showModal}
        selectedItems={encounters}
        connector="ALL"
        isEditing={encounters.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isSearching}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default EncountersComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderEncountersComponent = (
  container: HTMLElement,
  props: EncountersComponentProps & {
    initialEncounters?: EncounterItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <EncountersComponent {...props} />
    </Provider>
  );
  return root;
};
